import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { hideSlideoutMenu } from '@mm/state/actions/viewActions';
import { SelectedContact } from '@mm/state/interfaces/contacts';
import {
  connectedPageSelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useDetectOutsideClicks } from '../../../../../../common/hooks/use-detect-outside-clicks';
import { SlideoutMenu } from '../../../../../components/SlideoutMenu/components/SlideoutMenu';
import { ContactsDetails } from '../../../../contacts/components/ContactsDetails/ContactDetails';
import './ContactSlideoutMenu.pcss';

export type ContactSlideoutMenuProps = {
  className?: string;
  contactId?: number;
  selectedContact?: SelectedContact;
  withSessions?: boolean;
  deleteSelectedContact?: (
    contactId: number,
    eraseRemoteMessages: boolean,
  ) => Promise<void>;
  onContactSelect?: (contactId: number) => void;
  onContactUpdateSuccess?: () => void;
};

export const ContactSlideoutMenu = ({
  className,
  contactId,
  selectedContact,
  withSessions,
  onContactSelect = () => {},
  onContactUpdateSuccess = () => {},
  deleteSelectedContact = async () => {},
}: ContactSlideoutMenuProps) => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const connectedPage = useSelector(connectedPageSelector);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const busyNameOnMount = 'initial';
  const [contact, setContact] = useState<SelectedContact | undefined>(
    selectedContact?.dummy ? selectedContact : undefined,
  );
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState<string[]>([]);

  const contactsCRUDActions = useContactsCRUDActions();
  const fetchContact = useCallback(
    (busyComponent?: string) => {
      (async () => {
        if (
          contactId &&
          connectedPage &&
          activeCompanyId &&
          !selectedContact?.dummy
        ) {
          if (busyComponent) {
            setBusy([...busy, busyComponent]);
          }

          const contact = await contactsCRUDActions.retrieveContactById(
            activeCompanyId,
            contactId,
            connectedPage.id,
            true,
          );
          setBusy(busy.filter(str => str !== busyComponent));
          if (contact) {
            setContact(contact);
          }
        }
      })();
    },
    [contactId, connectedPage, activeCompanyId, contactsCRUDActions],
  );

  useEffect(() => fetchContact(busyNameOnMount), [contactId]);

  const onContactUpdate = (busyComponent?: string) => {
    fetchContact(busyComponent);
    onContactUpdateSuccess();
  };

  const onContactsClose = () => dispatch(hideSlideoutMenu());

  const setRef = (ref: HTMLElement) => {
    setWrapperRef(ref);
  };

  useDetectOutsideClicks(() => {
    onContactsClose();
  }, wrapperRef);

  if (!contactId) return null;

  return (
    <SlideoutMenu
      className={classNames(className, 'contact-slideout-menu')}
      setRef={setRef}
    >
      <SlideoutMenu.Content
        className="contact-slideout-menu__content"
        busy={busy.includes(busyNameOnMount)}
      >
        {contact && (
          <ContactsDetails
            closeDetails={onContactsClose}
            selectedContact={contact}
            onDeleteSelectedContact={deleteSelectedContact}
            onContactSelect={onContactSelect}
            refreshSelectedContact={onContactUpdate}
            withSessions={withSessions}
            busy={busy}
          />
        )}
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
